<template>
  <div class="user-activity-container">
    <el-card shadow="never">
      <div class="header-section">
        <h2 class="page-title">用戶活動監控</h2>
        <div class="search-section">
          <el-input
            v-model="searchQuery"
            placeholder="搜尋用戶名稱或 Email"
            prefix-icon="el-icon-search"
            clearable
            @input="handleSearch"
            style="width: 400px"
          />
          <el-button 
            type="primary" 
            icon="el-icon-refresh" 
            @click="refreshData"
            style="margin-left: 12px"
          >
            重新整理
          </el-button>
        </div>
      </div>

      <!--div class="stats-section">
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="stat-card">
              <div class="stat-label">總用戶數</div>
              <div class="stat-value">{{ totalUsers }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="stat-card">
              <div class="stat-label">活躍用戶（7天內）</div>
              <div class="stat-value active">{{ activeUsers }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="stat-card">
              <div class="stat-label">未活躍用戶</div>
              <div class="stat-value inactive">{{ inactiveUsers }}</div>
            </div>
          </el-col>
        </el-row>
      </div-->

      <el-table
        v-loading="loading"
        :data="paginatedFilteredUsers"
        @sort-change="handleSortChange"
        style="width: 100%"
        :default-sort="{ prop: 'lastAccess', order: 'descending' }"
        stripe
      >
        <el-table-column
          prop="name"
          label="用戶名稱"
          width="200"
          sortable
        >
          <template slot-scope="{ row }">
            <div class="user-info">
              <el-avatar :size="32" :src="row.avatar">
                {{ row.name ? row.name[0] : 'U' }}
              </el-avatar>
              <span class="user-name">{{ row.name || '未設定' }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          prop="email"
          label="Email"
          min-width="250"
          sortable
        />

        <el-table-column
          prop="role"
          label="角色"
          width="120"
          sortable
        >
          <template slot-scope="{ row }">
            <el-tag :type="getRoleType(row.role)" size="small">
              {{ getRoleLabel(row.role) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          prop="lastAccess"
          label="最後操作時間"
          width="200"
          sortable
          :sort-method="lastAccessSort"
          :sort-orders="['ascending','descending',null]"
        >
          <template slot-scope="{ row }">
            <div v-if="row.lastAccess" class="time-info">
              <i class="el-icon-time" style="margin-right: 4px"></i>
              <span>{{ formatDate(row.lastAccess) }}</span>
            </div>
            <span v-else class="no-data">無</span>
          </template>
        </el-table-column>

        <el-table-column
          label="活躍狀態"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag 
              :type="getActivityStatus(row.lastAccess).type" 
              size="small"
              effect="plain"
            >
              {{ getActivityStatus(row.lastAccess).label }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          label="距今時間"
          width="150"
        >
          <template slot-scope="{ row }">
            <span v-if="row.lastAccess" class="relative-time">
              {{ getRelativeTime(row.lastAccess) }}
            </span>
            <span v-else class="no-data">-</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100, 500]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredUsers.length"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      loading: false,
      searchQuery: '',
      userList: [],
      currentPage: 1,
      pageSize: 20,
      sortOrder: 'descending',
      sortProp: 'lastAccess',
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery) {
        return this.userList;
      }
      
      const query = this.searchQuery.toLowerCase();
      const filtered = this.userList.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });
      
      return filtered;
    },
    sortedUsers() {
      let users = [...this.filteredUsers];
      this.doSort(users);
      return users;
    },
    paginatedUsers() {
      return this.paginate(this.sortedUsers);
    },
    paginatedFilteredUsers() {
      return this.paginate(this.sortedUsers);
    },
    totalUsers() {
      return this.userList.length;
    },
    activeUsers() {
      const sevenDaysAgo = moment().subtract(7, 'days');
      return this.userList.filter(user => {
        return user.lastAccess && moment(user.lastAccess).isAfter(sevenDaysAgo);
      }).length;
    },
    inactiveUsers() {
      return this.totalUsers - this.activeUsers;
    },
  },
  mounted() {
    this.fetchUserActivity();
  },
  methods: {
    async fetchUserActivity() {
      this.loading = true;
      try {
        const res = await this.$axios({
          category: 'user',
          method: 'get',
          apiName: 'activity',
        });
        
        this.userList = res.data.map(user => ({
          _id: user._id,
          name: user.name,
          email: user.email,
          role: user.role || user.roles,
          lastAccess: user.lastAccess,
          avatar: user.avatar,
        }));
        
      } catch (error) {
        console.error('Error fetching user activity:', error);
        this.$message.error('載入用戶資料失敗');
      } finally {
        this.loading = false;
      }
    },
    refreshData() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.fetchUserActivity();
    },
    handleSearch() {
      this.currentPage = 1;
    },
    paginate(data) {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return data.slice(start, end);
      //return data;
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
    },
    handleCurrentChange(page) {
      this.currentPage = page;
    },
    formatDate(date) {
      if (!date) return '無';
      return moment(date).format('YYYY-MM-DD HH:mm:ss');
    },
    getRelativeTime(date) {
      if (!date) return '-';
      return moment(date).fromNow();
    },
    getActivityStatus(lastAccess) {
      if (!lastAccess) {
        return { type: 'info', label: '未活動' };
      }
      
      const hoursDiff = moment().diff(moment(lastAccess), 'hours');
      const daysDiff = moment().diff(moment(lastAccess), 'days');
      
      if (hoursDiff < 1) {
        return { type: 'success', label: '活躍中' };
      } else if (daysDiff < 7) {
        return { type: 'warning', label: '近期活躍' };
      } else {
        return { type: 'danger', label: '不活躍' };
      }
    },
    getRoleType(role) {
      const roleMap = {
        admin: 'danger',
        user: 'primary',
        manager: 'warning',
      };
      return roleMap[role] || 'info';
    },
    getRoleLabel(role) {
      const roleMap = {
        admin: '管理員',
        user: '一般用戶',
        manager: '管理者',
        occ: 'OCC',
        thsr: 'THSR',
        decl: 'DECL',
      };
      return roleMap[role] || role;
    },
    handleSortChange({ prop, order }) {
      if (!prop || !order) return;

      this.sortProp = prop;
      this.sortOrder = order;
      this.doSort(this.userList);
    },

    doSort(userList){
      const orderFactor = this.sortOrder === 'ascending' ? 1 : -1;

      // 對整份 userList 排序
      userList.sort((a, b) => {
        const valA = a[this.sortProp] ?? '';
        const valB = b[this.sortProp] ?? '';

        // 若為日期欄位
        if (this.sortProp === 'lastAccess') {
          let dateA = (new Date(valA)).getTime();
          let dateB = (new Date(valB)).getTime();
          const isValidA = !isNaN(dateA);
          const isValidB = !isNaN(dateB);
          // 若兩者皆非合法時間
          if (!isValidA && !isValidB) return 0;

          // 若 a 非時間字串 → 視為極小值
          if (!isValidA) return orderFactor * -1;
          // 若 b 非時間字串 → 視為極小值
          if (!isValidB) return orderFactor * 1;

          return (dateA - dateB) * orderFactor;
        }

        // 若為文字欄位
        if (typeof valA === 'string' && typeof valB === 'string') {
          return valA.localeCompare(valB) * orderFactor;
        }

        // 其他數值欄位
        return (valA - valB) * orderFactor;
      });
    },
    lastAccessSort(a, b) {
      const at = Date.parse(a.lastAccess);
      const bt = Date.parse(b.lastAccess);
  
      const aInvalid = Number.isNaN(at);
      const bInvalid = Number.isNaN(bt);
  
      if (aInvalid && !bInvalid) return -1;   // a > b
      if (!aInvalid && bInvalid) return 1;  // a < b
      if (aInvalid && bInvalid) return 0;    // 兩個都非法，視為相等
  
      // 都合法 → 比時間（升冪；降冪由 el-table 自行反轉）
      return at - bt;
    },
  },
};
</script>

<style scoped>
.user-activity-container {
  padding: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.search-section {
  display: flex;
  align-items: center;
}

.stats-section {
  margin-bottom: 24px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  border-radius: 8px;
  color: white;
  text-align: center;
}

.stat-card:nth-child(1) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
}

.stat-value.active {
  color: #67c23a;
}

.stat-value.inactive {
  color: #f56c6c;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-name {
  font-weight: 500;
}

.time-info {
  display: flex;
  align-items: center;
  color: #606266;
}

.no-data {
  color: #c0c4cc;
  font-style: italic;
}

.relative-time {
  color: #909399;
  font-size: 13px;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

:deep(.el-table) {
  font-size: 14px;
}

:deep(.el-table th) {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #606266;
}

:deep(.el-card__body) {
  padding: 24px;
}

:deep(.el-avatar) {
  background-color: #409eff;
  font-weight: 500;
}
</style>